import React, { useContext } from 'react';
import { Spinner as BsSpinner } from 'react-bootstrap';
import { ThemeContext } from '../../context/ThemeContext';

/**
 * Global Unified Loading Component for Rutex Backoffice
 * Provides standard, theme-aware loading states for Tables, Modals, Forms, and Views.
 * 
 * @param {string} size - 'sm' (20px), 'md' (32px), 'lg' (44px)
 * @param {string} title - Optional primary text message
 * @param {string} description - Optional secondary descriptive message
 * @param {string|number} minHeight - Container min-height (default: '180px')
 * @param {string} py - Vertical padding class (e.g. 'py-4', 'py-3')
 * @param {boolean} inline - If true, renders a compact inline spinner without full box
 * @param {object} style - Additional custom inline styles
 */
export function LoadingState({
  size = 'md',
  title = '',
  description = '',
  minHeight = '180px',
  py = 'py-4',
  inline = false,
  style = {}
}) {
  const { isDark } = useContext(ThemeContext) || {};

  // Spinner dimensions based on size prop - balanced & proportional
  const spinnerDimensions = {
    sm: { width: '1.25rem', height: '1.25rem', borderWidth: '2px' },
    md: { width: '2rem', height: '2rem', borderWidth: '2.8px' },
    lg: { width: '2.75rem', height: '2.75rem', borderWidth: '3.2px' }
  }[size] || { width: '2rem', height: '2rem', borderWidth: '2.8px' };

  // Spinner theme colors
  const spinnerColor = isDark ? '#818cf8' : '#183eeb';
  const titleColor = isDark ? '#f8fafc' : '#010028';
  const descColor = isDark ? '#94a3b8' : '#64748b';

  if (inline) {
    return (
      <span className="d-inline-flex align-items-center gap-2" style={style}>
        <BsSpinner
          animation="border"
          style={{
            ...spinnerDimensions,
            color: spinnerColor,
            borderRightColor: 'transparent'
          }}
        />
        {title && <span className="small fw-semibold" style={{ color: titleColor }}>{title}</span>}
      </span>
    );
  }

  return (
    <div
      className={`w-100 d-flex flex-column align-items-center justify-content-center text-center ${py}`}
      style={{
        minHeight,
        backgroundColor: 'transparent',
        ...style
      }}
    >
      <div className="position-relative d-flex align-items-center justify-content-center mb-2">
        <BsSpinner
          animation="border"
          role="status"
          style={{
            ...spinnerDimensions,
            color: spinnerColor,
            borderRightColor: 'transparent'
          }}
        />
      </div>

      {title && (
        <h6 className="fw-semibold mb-1" style={{ color: titleColor, fontSize: size === 'sm' ? '0.82rem' : '0.9rem', letterSpacing: '0.2px' }}>
          {title}
        </h6>
      )}

      {description && (
        <p className="small mb-0" style={{ color: descColor, maxWidth: '420px', fontSize: '0.78rem' }}>
          {description}
        </p>
      )}
    </div>
  );
}

export default LoadingState;
